import React, { useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { getMyChamCongSummary, getMyTable, getMyThuNhapSummary, getMyKhaoSatTongHop, getMyTuTkb, getMyCongDaoTao } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const VIPHAM_TABLE_KEY = "viphamGV"; // đúng tableKey của bảng "Vi phạm — GV Văn hoá" (admin nhập)
// 3 bảng "Khảo sát" — mỗi bảng đúng 1 sheet của file Excel gốc (admin nhập ở
// trang "Khảo sát"), nhãn hiển thị khớp tên sheet.
const KHAO_SAT_TABLE_KEYS = [
  { key: "khaoSatLan1K12", label: "Lần 1 Khối 12 (T10/2026)" },
  { key: "khaoSatLan2K10", label: "Lần 2 Khối 10" },
  { key: "khaoSatLan3K12", label: "Lần 3 Khối 12" },
  { key: "khaoSatLan4K12", label: "Lần 4 Khối 12" },
];

// YYYY-MM-DD -> DD/MM
const fmt = (d) => {
  if (!d) return "";
  const p = String(d).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : d;
};
const vnd = (n) => Number(n || 0).toLocaleString("vi-VN");

// Webview nhúng trong Zalo/Messenger/Instagram/Line/WeChat CHỈ chặn tải file
// (Blob download) trên iOS (WKWebView giới hạn) — Android webview vẫn tải
// bình thường qua Download Manager của hệ thống nên không cần chặn.
function isBlockedInAppBrowser() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isInApp = /Zalo|FBAN|FBAV|Instagram|Line\/|MicroMessenger/i.test(ua);
  return isIOS && isInApp;
}
async function downloadExcelJsWorkbook(wb, fileName) {
  if (isBlockedInAppBrowser()) {
    window.alert(
      "Trình duyệt trong ứng dụng (Zalo/Messenger/...) trên iPhone không cho phép tải file.\n\n" +
      "Hãy bấm nút menu (⋮ hoặc ...) ở góc trên, chọn \"Mở bằng trình duyệt\" (Safari), rồi bấm Xuất Excel lại."
    );
    return;
  }
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
// Áp Times New Roman + kẻ ô (viền mỏng) cho MỌI ô đã có dữ liệu trong 1 sheet.
function styleSheetTimesNewRomanBordered(ws) {
  ws.eachRow({ includeEmpty: true }, (row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { name: "Times New Roman", size: 12, bold: cell.font && cell.font.bold };
      cell.border = {
        top: { style: "thin" }, left: { style: "thin" },
        bottom: { style: "thin" }, right: { style: "thin" },
      };
    });
  });
}

// Báo các tháng nhà trường CHƯA công bố lương (GV chỉ xem được số tiết ở "Xem chấm công").
const ChuaCongBoNote = ({ list }) => (list && list.length ? (
  <p style={{ color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 10, fontSize: 13 }}>
    🔒 Nhà trường chưa công bố lương: {list.map((x) => x.label).join(", ")}. Khi công bố bạn sẽ xem được ở đây; hiện chỉ xem được số tiết dạy ở tab "Xem chấm công".
  </p>
) : null);

// Tab "Xem chấm công" — tổng hợp mọi tuần theo BẢN TKB ĐÃ CÔNG BỐ (không phải
// bảng gõ tay), hiển thị dạng cây: mỗi tháng 1 node, bên trong là các tuần.
const ChamCongTab = () => {
  const schoolId = getActiveSchoolId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openMonths, setOpenMonths] = useState({});

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    setError("");
    getMyChamCongSummary(schoolId)
      .then((d) => {
        setData(d);
        if (d && d.months && d.months.length) {
          setOpenMonths({ [d.months[d.months.length - 1].monthKey]: true });
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId]);

  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (!data) return null;

  const toggle = (key) => setOpenMonths((x) => ({ ...x, [key]: !x[key] }));
  const { months, grandTotal } = data;

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Tổng hợp theo thời khoá biểu <b>cuối cùng đã công bố</b> của mỗi tuần — {data.name}
        {data.employmentType ? ` (${data.employmentType})` : ""}. Tuần chưa được nhà trường công bố bản nào sẽ hiện "chưa công bố".
      </p>

      <div style={grandBox}>
        <div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Tổng tiết (thường / TC)</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{grandTotal.tietThuong} / {grandTotal.tietTC}</div>
        </div>
        {months.some((m) => !m.moneyHidden) && (
          <div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Tổng tiền (các tháng đã công bố)</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f766e" }}>{vnd(grandTotal.tien)}</div>
          </div>
        )}
      </div>
      {data.coThangChuaCongBo && (
        <p style={{ color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 10, fontSize: 13, marginTop: 0 }}>
          🔒 Tháng có biểu tượng khoá là tháng nhà trường <b>chưa công bố lương</b> — chỉ hiện số tiết dạy. Khi công bố mới xem được tiền.
        </p>
      )}

      {months.length === 0 ? (
        <p style={{ color: "#6b7280" }}>Chưa có tuần thời khoá biểu nào.</p>
      ) : (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
          {months.map((m) => (
            <div key={m.monthKey} style={{ borderTop: "1px solid #e5e7eb" }}>
              <div onClick={() => toggle(m.monthKey)} style={monthRow}>
                <span style={{ width: 14 }}>{openMonths[m.monthKey] ? "▾" : "▸"}</span>
                <span style={{ fontWeight: 700, flex: 1 }}>📅 {m.label}</span>
                <span style={{ fontSize: 13, color: "#6b7280", marginRight: 12 }}>{m.sumTietThuong + m.sumTietTC} tiết</span>
                {m.moneyHidden
                  ? <span style={{ fontSize: 12, color: "#92400e" }}>🔒 Chưa công bố lương</span>
                  : <span style={{ fontWeight: 700, color: "#0f766e" }}>{vnd(m.sumTien)}</span>}
              </div>
              {openMonths[m.monthKey] && m.payrollGroup === "dinhmuc17" ? (
                <div>
                  <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={th}>Tuần</th>
                        <th style={th}>Tiết dạy</th>
                        <th style={th}>Tiết tiêu chuẩn</th>
                        <th style={th}>Tiết vượt tiêu chuẩn</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.weeks.map((w) => (
                        <tr key={w.weekId}>
                          <td style={{ ...td, textAlign: "left" }}>{fmt(w.startDate)}–{fmt(w.endDate)}</td>
                          <td style={td}>{w.tietThuong}</td>
                          <td style={td}>{w.tieuChuanTuan}</td>
                          <td style={{ ...td, color: w.vuotTieuChuanTuan > 0 ? "#0f766e" : "#6b7280" }}>
                            {w.vuotTieuChuanTuan > 0 ? `+${w.vuotTieuChuanTuan}` : 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!m.moneyHidden && (
                    <p style={{ fontSize: 12, color: "#6b7280", padding: "8px 12px", margin: 0, background: "#f9fafb" }}>
                      Trả theo định mức tháng, không phải tiết×đơn giá: {m.formulaText}
                    </p>
                  )}
                </div>
              ) : openMonths[m.monthKey] && (
                <div>
                  <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={th}>Tuần</th>
                        <th style={th}>Tiết thường</th>
                        <th style={th}>Tiết TC</th>
                        {!m.moneyHidden && <th style={th}>Thành tiền</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {m.weeks.map((w) => (
                        <tr key={w.weekId}>
                          <td style={{ ...td, textAlign: "left" }}>
                            {fmt(w.startDate)}–{fmt(w.endDate)}
                            {w.source === "manual" && <span style={{ color: "#b45309", fontSize: 11 }}> (theo bảng tháng)</span>}
                          </td>
                          {w.published ? (
                            <>
                              <td style={td}>{w.tietThuong}</td>
                              <td style={td}>{w.tietTC}</td>
                              {!m.moneyHidden && <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{vnd(w.tien)}</td>}
                            </>
                          ) : (
                            <td style={{ ...td, color: "#9ca3af" }} colSpan={m.moneyHidden ? 2 : 3}>Chưa công bố</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Tab "Vi phạm" — bảng nhà trường nhập tay (mỗi GV chỉ thấy đúng dòng khớp
// tên hồ sơ của mình, và chỉ khi đã công bố) — dùng chung hạ tầng bảng dữ
// liệu tuỳ biến (như "Chấm công tháng").
const ViPhamTab = () => {
  const schoolId = getActiveSchoolId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    setError("");
    getMyTable(schoolId, VIPHAM_TABLE_KEY)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId]);

  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (!data) return null;

  const cols = (data.columns || []).filter((c) => c.key !== "stt");

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Chỉ hiện đúng dòng vi phạm khớp tên hồ sơ Nhân sự đã liên kết với tài khoản của bạn, và chỉ khi nhà trường đã công bố.
      </p>
      {!data.published ? (
        <p style={{ color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 12 }}>
          Nhà trường chưa công bố dữ liệu vi phạm.
        </p>
      ) : data.rows.length === 0 ? (
        <p style={{ color: "#6b7280" }}>Không có vi phạm nào được ghi nhận.</p>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
            <thead>
              <tr>{cols.map((c) => (<th key={c.key} style={th}>{c.label}</th>))}</tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id}>{cols.map((c) => (<td key={c.key} style={td}>{r[c.key] ?? ""}</td>))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Tab "Thu nhập": chỉ GỌI API và hiển thị — toàn bộ công thức (phí CK ngân
// hàng, thuế TNCN 10%/hỗ trợ 5% cho Thỉnh giảng, trừ vi phạm theo tháng) đã
// tính sẵn Ở SERVER (/me/thunhap-summary), tránh để logic tiền bạc nằm ở
// frontend (có thể bị xem/sửa qua devtools, và dễ lệch nếu 2 nơi tính khác
// công thức).
// Các khoản CỘNG ngoài chấm công — chỉ hiện cột khi ít nhất 1 tháng có số > 0
// (giáo viên thường không có thì bảng vẫn gọn như cũ).
const INCOME_EXTRA_COLS = [
  { key: "hotroTinHoc", label: "Hỗ trợ Tin học" },
  { key: "phuCapChuyenMon", label: "Chuyên môn" },
  { key: "phuCapChucVu", label: "Chức vụ/GVCN" },
  { key: "phuCapDienThoai", label: "Điện thoại" },
  { key: "phuCapXangXe", label: "Xăng xe" },
  { key: "phuCapAn", label: "Ăn" },
  { key: "phuCapKhac", label: "Phụ cấp khác" },
  { key: "luongCaDem", label: "Lương ca đêm" },
  { key: "kiemXe", label: "Tiền kiểm xe" },
  { key: "thuNhapKhac", label: "Các thu nhập khác" },
];

// Tổng thu nhập (trước mọi khoản trừ) = Lương theo tiết + Khảo sát + tất cả
// phụ cấp/hỗ trợ — tương ứng cột "Tổng thu nhập" của bảng lương Đào tạo.
const tongThuNhapOf = (r) =>
  (Number(r.tongChamCong) || 0) + (Number(r.khaoSat) || 0) + INCOME_EXTRA_COLS.reduce((s, c) => s + (Number(r[c.key]) || 0), 0);

// Diễn giải lương theo ngày công (Lương cơ bản × tỷ lệ × ngày làm/ngày chuẩn) —
// chỉ có khi admin nhập lương cơ bản + ngày công ở bảng Chấm công tháng.
const luongNgayCongText = (r) => {
  const cb = Number(r.luongCoBan) || 0, chuan = Number(r.congChuan) || 0, lam = Number(r.congThucTe) || 0, ty = Number(r.tyLeHuong) || 0;
  if (cb <= 0 || chuan <= 0 || lam <= 0) return "";
  return `${cb.toLocaleString("vi-VN")}${ty > 0 && ty < 100 ? ` × ${ty}%` : ""} × ${lam}/${chuan} ngày`;
};

const ThuNhapTab = () => {
  const schoolId = getActiveSchoolId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    setError("");
    getMyThuNhapSummary(schoolId)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId]);

  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (!data) return null;

  const { isTG, months, truKhongRoThang, grandThucNhan, chuaCongBo } = data;
  const has = (key) => months.some((r) => (r[key] || 0) > 0);

  // Danh sách cột (sau cột "Tháng", trước "Thực nhận") theo đúng thứ tự công
  // thức: Lương theo tiết, Khảo sát, các khoản cộng, các khoản trừ. kind:
  // base = số gốc, plus = cộng (xanh), minus = trừ (đỏ). id dùng để dựng công
  // thức Excel bên dưới.
  const hasBasis = has("luongCoBan");
  const cols = [
    ...(hasBasis ? [
      { id: "luongCoBan", label: "Lương cơ bản", kind: "info", get: (r) => r.luongCoBan },
      { id: "ngayCong", label: "Ngày công (làm/chuẩn)", kind: "info", get: (r) => r.congThucTe, text: (r) => (r.congChuan > 0 ? `${r.congThucTe}/${r.congChuan}` : "") },
      ...(has("tyLeHuong") ? [{ id: "tyLeHuong", label: "Tỷ lệ hưởng", kind: "info", get: (r) => r.tyLeHuong, text: (r) => (r.tyLeHuong > 0 ? `${r.tyLeHuong}%${r.luongGhiChu ? ` (${r.luongGhiChu})` : ""}` : "") }] : []),
    ] : []),
    { id: "luong", label: hasBasis ? "Lương theo ngày công" : "Lương, PC giảng dạy", kind: "base", get: (r) => r.tongChamCong },
    { id: "khaoSat", label: "Ra đề, chấm thi KS", kind: "plus", get: (r) => r.khaoSat },
    ...INCOME_EXTRA_COLS.filter((c) => c.key !== "thuNhapKhac" && has(c.key)).map((c) => ({ id: c.key, label: c.label, kind: "plus", get: (r) => r[c.key] })),
    ...(has("thuNhapKhac") ? [{ id: "thuNhapKhac", label: "Các thu nhập khác", kind: "plus", get: (r) => r.thuNhapKhac, note: (r) => r.thuNhapKhacNoiDung }] : []),
    ...(has("quaTang") ? [{ id: "quaTang", label: "🎁 Quà tặng", kind: "plus", get: (r) => r.quaTang, note: (r) => r.quaTangNoiDung }] : []),
    ...(has("quaTangNT") ? [{ id: "quaTangNT", label: "🎁 Quà Nhà trường", kind: "plus", get: (r) => r.quaTangNT }] : []),
    { id: "tongThuNhap", label: "Tổng thu nhập", kind: "total", get: (r) => tongThuNhapOf(r) + (Number(r.quaTang) || 0) + (Number(r.quaTangNT) || 0) },
    { id: "tongTru", label: "Trừ vi phạm", kind: "minus", get: (r) => r.tongTru },
    ...(has("khoanTruKhac") ? [{ id: "khoanTruKhac", label: "Khoản trừ khác", kind: "minus", get: (r) => r.khoanTruKhac, note: (r) => r.khoanTruKhacNoiDung }] : []),
    ...(has("tamUng") ? [{ id: "tamUng", label: "💸 Tạm ứng lương", kind: "minus", get: (r) => r.tamUng }] : []),
    { id: "phiCK", label: "Phí CK", kind: "minus", get: (r) => r.phiCK },
    ...(has("bhxh") || has("phiCongDoan") ? [
      { id: "bhxh", label: "BHXH", kind: "minus", get: (r) => r.bhxh },
      { id: "phiCongDoan", label: "Phí công đoàn", kind: "minus", get: (r) => r.phiCongDoan },
    ] : []),
    ...(has("thueTNCNTru") ? [{ id: "thueTNCNTru", label: "Thuế TNCN", kind: "minus", get: (r) => r.thueTNCNTru, note: (r) => (Math.abs(r.thueTNCNTru - 0.1 * tongThuNhapOf(r)) < 1 ? "10% thu nhập" : "") }] : []),
    ...(isTG ? [
      { id: "thueAuto", label: "Thuế TNCN", kind: "minus", get: (r) => r.thueTNCN },
      { id: "hoTroAuto", label: "Hỗ trợ", kind: "plus", get: (r) => r.hoTro },
    ] : []),
  ];
  const colSpanBeforeThucNhan = 1 + cols.length;

  const renderCell = (c, r) => {
    const v = Number(c.get(r)) || 0;
    if (c.kind === "info") {
      const t = c.text ? c.text(r) : (v > 0 ? `${vnd(v)}` : "");
      return <td key={c.id} style={{ ...td, color: "#374151" }}>{t || "—"}</td>;
    }
    if (c.kind === "base") {
      return (
        <td key={c.id} style={td}>
          {vnd(v)}
          {luongNgayCongText(r) && (
            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 400, marginTop: 2 }}>{luongNgayCongText(r)}</div>
          )}
          {r.payrollGroup === "dinhmuc17" && r.formulaText && (
            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 400, marginTop: 2 }}>{r.formulaText}</div>
          )}
        </td>
      );
    }
    if (c.kind === "total") return <td key={c.id} style={{ ...td, fontWeight: 700, background: "#f0fdf4" }}>{vnd(v)}</td>;
    const color = v > 0 ? (c.kind === "plus" ? "#0f766e" : "#dc2626") : "#6b7280";
    const sign = c.kind === "plus" ? "+" : "-";
    return (
      <td key={c.id} style={{ ...td, color }}>
        {v > 0 ? `${sign}${vnd(v)}` : "0"}
        {c.note && v > 0 && c.note(r) && (
          <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 400, marginTop: 2 }}>{c.note(r)}</div>
        )}
      </td>
    );
  };

  // Xuất Excel với CÔNG THỨC thật (không phải số tĩnh) — sửa số trong file thì
  // Thuế/Hỗ trợ/Thực nhận tự tính lại: Sau CK = Lương − Trừ vi phạm − Phí CK −
  // BHXH − Phí công đoàn; Thuế = Sau CK×10% và Hỗ trợ = Sau CK×5% (chỉ Thỉnh
  // giảng); Thực nhận = Sau CK − Thuế + Hỗ trợ + Khảo sát + các khoản cộng −
  // Khoản trừ khác − Thuế TNCN trừ trực tiếp.
  const colLetter = (n) => String.fromCharCode(64 + n);
  const exportExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Thu nhap");
    const letterOf = {};
    cols.forEach((c, i) => { letterOf[c.id] = colLetter(i + 2); });
    const thucNhanCol = cols.length + 2;
    ws.addRow([`XEM THU NHẬP — ${data.name}${data.employmentType ? ` (${data.employmentType})` : ""}`]);
    ws.addRow(["Tháng", ...cols.map((c) => c.label), "Thực nhận"]);
    const dataRows = months.map((m) => {
      const row = ws.addRow([m.label, ...cols.map((c) => (c.id === "thueAuto" || c.id === "hoTroAuto" ? null : (c.text ? c.text(m) : (Number(c.get(m)) || 0)))), null]);
      const r = row.number;
      const L = (id) => (letterOf[id] ? `${letterOf[id]}${r}` : null);
      row.getCell(letterOf.tongThuNhap.charCodeAt(0) - 64).value = {
        formula: ["luong", "khaoSat", ...INCOME_EXTRA_COLS.map((c) => c.key)].map(L).filter(Boolean).join("+"),
      };
      const sauCK = ["luong"].map(L).concat(["khaoSat"].map(L).filter(Boolean).map((x) => `+${x}`)).concat(["tongTru", "phiCK", "bhxh", "phiCongDoan"].map(L).filter(Boolean).map((x) => `-${x}`)).join("");
      const plus = [...INCOME_EXTRA_COLS.map((c) => c.key)].map(L).filter(Boolean).map((x) => `+${x}`).join("");
      const minusAfter = ["khoanTruKhac", "tamUng", "thueTNCNTru"].map(L).filter(Boolean).map((x) => `-${x}`).join("");
      if (isTG) {
        row.getCell(cols.findIndex((c) => c.id === "thueAuto") + 2).value = { formula: `(${sauCK})*0.1` };
        row.getCell(cols.findIndex((c) => c.id === "hoTroAuto") + 2).value = { formula: `(${sauCK})*0.05` };
        row.getCell(thucNhanCol).value = { formula: `(${sauCK})-${L("thueAuto")}+${L("hoTroAuto")}${plus}${minusAfter}` };
      } else {
        row.getCell(thucNhanCol).value = { formula: `${sauCK}${plus}${minusAfter}` };
      }
      return row;
    });
    if (truKhongRoThang > 0) {
      const label = Array(thucNhanCol - 1).fill("");
      label[0] = "Vi phạm không rõ ngày/tháng";
      const row = ws.addRow([...label, -truKhongRoThang]);
      row.getCell(thucNhanCol).numFmt = "#,##0";
    }
    if (dataRows.length > 0) {
      const first = dataRows[0].number;
      const last = dataRows[dataRows.length - 1].number;
      const totalLabel = Array(thucNhanCol - 1).fill("");
      totalLabel[0] = "Tổng cộng thực nhận";
      const totalRow = ws.addRow([...totalLabel, null]);
      const tl = colLetter(thucNhanCol);
      const sumRange = truKhongRoThang > 0
        ? `SUM(${tl}${first}:${tl}${last})-${tl}${last + 1}`
        : `SUM(${tl}${first}:${tl}${last})`;
      totalRow.getCell(thucNhanCol).value = { formula: sumRange };
    }
    ws.columns.forEach((c) => { c.width = 20; });
    ws.getColumn(1).width = 16;
    styleSheetTimesNewRomanBordered(ws);
    await downloadExcelJsWorkbook(wb, "xem_thu_nhap.xlsx");
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button type="button" onClick={exportExcel} style={exportBtn}>⬇ Xuất Excel</button>
      </div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Mỗi tháng: Chấm công + Khảo sát + các khoản phụ cấp/hỗ trợ (nếu có) − Vi phạm trong tháng − Khoản trừ khác − Phí CK ngân hàng − BHXH/Phí công đoàn (nếu có)
        {isTG ? " − Thuế TNCN 10% + Nhà trường hỗ trợ 5% (Thỉnh giảng)" : " (Cơ hữu không bị trừ thuế TNCN tự động)"}
        {" "}(Khảo sát/phụ cấp cộng thẳng, không qua phí/thuế — Khảo sát chỉ hiện ở tháng gần nhất đã kết thúc).
      </p>
      {!data.bankName && (
        <p style={{ color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 10, fontSize: 13 }}>
          Bạn chưa khai "Ngân hàng nhận lương" ở Hồ sơ của tôi — đang tạm tính phí CK theo mức ngân hàng khác cho mỗi tháng.
        </p>
      )}
      <ChuaCongBoNote list={chuaCongBo} />
      {months.length === 0 ? (
        <p style={{ color: "#6b7280" }}>{chuaCongBo && chuaCongBo.length ? "Chưa có tháng nào được công bố." : "Chưa có tháng nào để tính."}</p>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Tháng</th>
                {cols.map((c) => (<th key={c.id} style={th}>{c.label}</th>))}
                <th style={th}>Thực nhận</th>
              </tr>
            </thead>
            <tbody>
              {months.map((r) => (
                <tr key={r.monthKey}>
                  <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{r.label}</td>
                  {cols.map((c) => renderCell(c, r))}
                  <td style={{ ...td, fontWeight: 700, color: "#047857" }}>{vnd(r.thucNhan)}</td>
                </tr>
              ))}
              {truKhongRoThang > 0 && (
                <tr>
                  <td style={{ ...td, textAlign: "left", color: "#92400e" }} colSpan={colSpanBeforeThucNhan}>Vi phạm không rõ ngày/tháng</td>
                  <td style={{ ...td, color: "#dc2626" }}>-{vnd(truKhongRoThang)}</td>
                </tr>
              )}
              <tr>
                <td style={{ ...td, textAlign: "left", fontWeight: 700, background: "#ecfdf5" }} colSpan={colSpanBeforeThucNhan}>Tổng cộng thực nhận</td>
                <td style={{ ...td, fontWeight: 700, fontSize: 15, color: "#047857", background: "#ecfdf5" }}>{vnd(grandThucNhan)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Tab con "Khảo sát" (1 trong 3 sheet chi tiết) — bảng chỉ đọc, đúng dòng
// khớp tên hồ sơ của mình, tái dùng /me/table (giống ViPhamTab).
const KhaoSatSheetTab = ({ tableKey }) => {
  const schoolId = getActiveSchoolId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    setError("");
    getMyTable(schoolId, tableKey)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, tableKey]);

  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (!data) return null;

  const cols = (data.columns || []).filter((c) => c.key !== "stt");

  return !data.published ? (
    <p style={{ color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 12 }}>
      Nhà trường chưa công bố đợt khảo sát này.
    </p>
  ) : data.rows.length === 0 ? (
    <p style={{ color: "#6b7280" }}>Không có dòng nào khớp tên bạn trong đợt này.</p>
  ) : (
    <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
        <thead><tr>{cols.map((c) => (<th key={c.key} style={th}>{c.label}</th>))}</tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.id}>{cols.map((c) => (<td key={c.key} style={td}>{c.key === "thucnhan" ? `${vnd(r[c.key])}` : (r[c.key] ?? "")}</td>))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Tab con "Tổng hợp" — tự cộng lại (server tính), không lấy số Tổng sẵn có
// trong Excel gốc vì Excel tính sai (VD dòng "Đã chuyển công tác" bị cộng
// nhầm vào tổng sheet).
const KhaoSatTongHopTab = () => {
  const schoolId = getActiveSchoolId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    setError("");
    getMyKhaoSatTongHop(schoolId).then(setData).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [schoolId]);

  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (!data) return null;

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>Tổng Thực nhận trên các đợt khảo sát đã công bố.</p>
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%", border: "1px solid #e5e7eb", borderRadius: 8 }}>
        <thead>
          <tr>
            {KHAO_SAT_TABLE_KEYS.map((t) => (<th key={t.key} style={th}>{t.label}</th>))}
            <th style={th}>Tổng</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            {KHAO_SAT_TABLE_KEYS.map((t) => (<td key={t.key} style={td}>{data.byLan[t.key] > 0 ? `${vnd(data.byLan[t.key])}` : "—"}</td>))}
            <td style={{ ...td, fontWeight: 700, color: "#047857" }}>{vnd(data.tong)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const KHAO_SAT_SUB_TABS = [...KHAO_SAT_TABLE_KEYS, { key: "tonghop", label: "Tổng hợp" }];

const KhaoSatTab = () => {
  const [sub, setSub] = useState(KHAO_SAT_TABLE_KEYS[0].key);
  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {KHAO_SAT_SUB_TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setSub(t.key)} style={t.key === sub ? subTabBtnActive : subTabBtnInactive}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === "tonghop" ? <KhaoSatTongHopTab /> : <KhaoSatSheetTab tableKey={sub} />}
    </div>
  );
};

// Tab "Lương theo tiết": đúng dữ liệu "Chấm công theo TKB" của admin, nhưng chỉ dòng của chính người đăng nhập.
const isThinhGiangLoai = (loai) => String(loai || "").normalize("NFC").trim() === "Thỉnh giảng";
const monthTitle = (mk) => `Tháng ${Number(mk.slice(5, 7))}/${mk.slice(0, 4)}`;
const LuongTheoTietTab = () => {
  const schoolId = getActiveSchoolId();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!schoolId) return;
    getMyTuTkb(schoolId).then(setData).catch((e) => setError(e.message));
  }, [schoolId]);
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (!data) return <p style={{ color: "#6b7280" }}>Đang tải...</p>;
  if (data.months.length === 0) return (
    <div>
      <ChuaCongBoNote list={data.chuaCongBo} />
      <p style={{ color: "#6b7280" }}>Chưa có dữ liệu lương và PC giảng dạy của bạn. Số liệu tính từ tháng 9/2026 và chỉ hiện sau khi tháng kết thúc và được nhà trường công bố.</p>
    </div>
  );

  const rowsOf = (r) => {
    if (r.nghe) {
      // Dòng của mình trong "🛠 Chấm công GV Nghề" (GV Nghề không có TKB K12).
      if (r.fullTime) {
        return [["Loại", "GV Nghề (fulltime)"], ["Tiết ngoài TKB", r.tiet], ["Lương tháng", vnd(r.tien)],
          ["Xăng xe", r.xangXe ? vnd(r.xangXe) : "—"], ["PC Ăn", r.pcAn ? vnd(r.pcAn) : "—"]];
      }
      const lhq = (Number(r.tien) || 0) - (Number(r.luongCoBan) || 0) - (Number(r.ltv) || 0) - (Number(r.htdt) || 0);
      return [["Loại", "GV Nghề"], ["Tiết ngoài TKB", r.tiet], ["Tiết chủ nhiệm", r.tietCN || "—"],
        ["Tổng số tiết", r.tong], [`Tiết tiêu chuẩn`, r.tietChuan], ["Tiết vượt", r.tong - r.tietChuan],
        ["Lương cơ bản", vnd(r.luongCoBan)], ["Lương thâm niên + Chất lượng công việc", lhq ? vnd(lhq) : "—"],
        [`Lương tiết vượt (×${vnd(Number(r.donGia) || 0)})`, vnd(r.ltv)], ["HT ĐT", r.htdt ? vnd(r.htdt) : "—"],
        ["Hỗ trợ Tin học", r.htTinHoc ? vnd(r.htTinHoc) : "—"], ["Phụ cấp đào tạo", r.pcDaoTao ? vnd(r.pcDaoTao) : "—"],
        ["Xăng xe", r.xangXe ? vnd(r.xangXe) : "—"], ["PC Ăn", r.pcAn ? vnd(r.pcAn) : "—"]];
    }
    const out = [["Loại", r.loai || "—"]];
    if (r.fullTime) {
      out.push(["Số tuần có TKB", r.soTuan], ["Tiết thường", r.tietThuong], ["Tiết TC", r.tietTC], ["Tiết coi thi KS", r.tietKS || "—"]);
      if (r.ftDinhMuc) {
        out.push(...((r.tietCN1 || 0) + (r.tietCN2 || 0) > 0 ? [["Tiết chủ nhiệm (lớp 1 + lớp 2)", `${(r.tietCN1 || 0) + (r.tietCN2 || 0)} (${r.tietCN1 || 0} + ${r.tietCN2 || 0})`]] : []),
          ["Tổng số tiết" + ((r.tietCN1 || 0) + (r.tietCN2 || 0) > 0 ? " (CK+TC+CN+KS)" : " (CK+TC+KS)"), r.tongTiet], ["Tiết định mức", r.tietChuan], ["Tiết vượt", r.tietVuot || 0],
          [`Tiền vượt định mức (×${vnd(r.donGiaTietVuotFT || 80000)})`, vnd(r.tienTietVuotFT)]);
      }
      out.push(["Lương tháng (đủ công)", vnd(r.luongNgayCong)], ["Ngày công chuẩn", r.congChuan], ["Ngày công thực tế", r.congThucTe]);
    } else if (isThinhGiangLoai(r.loai)) {
      out.push(["Số tuần có TKB", r.soTuan], ["Tiết thường", r.tietThuong], ["Tiết TC", r.tietTC],
        ["Tiết coi thi KS" + (r.ksNoiDung ? ` (${r.ksNoiDung})` : ""), r.tietKS || "—"],
        ["Đơn giá KS", r.tietKS ? vnd(r.donGiaKS || 80000) : "—"],
        ["Thành tiền KS", r.tietKS ? vnd(r.tienKS) : "—"]);
    } else {
      out.push(["Số tuần có TKB", r.soTuan], ["Tiết thường", r.tietThuong], ["Tiết TC", r.tietTC],
        ["Số lớp CN", r.soLopCN || "—"],
        ["Tiết CN Lớp 1", r.lopCN1 ? `${r.tietCN1} (${r.lopCN1})` : "—"],
        ["Tiết CN Lớp 2", r.lopCN2 ? `${r.tietCN2} (${r.lopCN2})` : "—"],
        ["Tiết coi thi KS" + (r.ksNoiDung ? ` (${r.ksNoiDung})` : ""), r.tietKS || "—"],
        ["Số lớp dạy TN12", r.soLopTN12 || "—"],
        ["Tổng số tiết (CK+TC+CN+KS)", r.tongTiet],
        ["Tiết tiêu chuẩn (4×17)", r.tietChuan || "—"],
        ["Tiết vượt", r.tietChuan ? r.tietVuot : "—"],
        ["Lương cơ bản", r.luongCoBan ? vnd(r.luongCoBan) : "—"],
        ["Lương thâm niên + Chất lượng công việc", r.luongHieuQua ? vnd(r.luongHieuQua) : "—"],
        [`Lương tiết vượt (×${vnd(Number(r.donGiaTietVuot) || 80000)}${r.namThu ? `, năm thứ ${r.namThu}` : ""})`, r.tietChuan ? vnd(r.tietVuot * (Number(r.donGiaTietVuot) || 80000)) : "—"],
        ["Tiền TN12 (×100.000)", r.tienTN12 ? vnd(r.tienTN12) : "—"]);
    }
    if (r.fullTime || isThinhGiangLoai(r.loai)) out.push(["Số lớp CN", r.soLopCN || "—"]);
    out.push(["HT ĐT (200.000 / lớp CN)", r.tienDienThoai ? vnd(r.tienDienThoai) : "—"]);
    return out;
  };

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Tự tính từ TKB đã công bố của từng tuần — cùng số liệu với bảng "Chấm công theo TKB" (GV Nghề: bảng "🛠 Chấm công GV Nghề") của nhà trường, chỉ gồm phần của bạn.
      </p>
      <ChuaCongBoNote list={data.chuaCongBo} />
      {data.months.map(({ monthKey, row }) => (
        <div key={monthKey + (row.nghe ? "-nghe" : "")} style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 14, overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
            <b>{monthTitle(monthKey)} — {row.hoten}{row.nghe ? " · 🛠 Chấm công GV Nghề" : ""}</b>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#047857" }}>{row.nghe ? "Tổng nhận" : "Tiền"}: {vnd(row.nghe ? row.tongNhan : row.tien)}</span>
          </div>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
            <tbody>
              {rowsOf(row).map(([k, v]) => (
                <tr key={k}>
                  <td style={{ padding: "6px 14px", borderBottom: "1px solid #f3f4f6", color: "#6b7280" }}>{k}</td>
                  <td style={{ padding: "6px 14px", borderBottom: "1px solid #f3f4f6", textAlign: "right", fontWeight: 600 }}>{v}</td>
                </tr>
              ))}
              <tr>
                <td style={{ padding: "8px 14px", background: "#ecfdf5", fontWeight: 700 }}>{row.nghe ? "Tổng nhận" : "Tiền"}</td>
                <td style={{ padding: "8px 14px", background: "#ecfdf5", textAlign: "right", fontWeight: 700, color: "#047857", fontSize: 16 }}>{vnd(row.nghe ? row.tongNhan : row.tien)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

// 🏫 Thu nhập Đào tạo — phiếu lương theo tháng từ bảng Công Đào Tạo (+ QTKD nếu có).
const CDT_LINES = [
  ["luongCoBan", "Lương cơ bản"], ["cong", "Ngày công (thoả thuận / thực tế)"], ["luongNgayCong", "Lương theo ngày công", "b"],
  ["pcDungLop", "PC Đứng lớp"], ["pcChuyenMon", "PC Chuyên môn"], ["pcDienThoai", "PC Điện thoại"], ["pcChucVu", "PC Chức vụ / GVCN"], ["pcXangXe", "PC Xăng xe"], ["pcAn", "PC Ăn"], ["pcKhac", "PC Khác"],
  ["pcTong", "Tổng phụ cấp", "b"], ["quaTang", "🎁 Quà 20-10 (HS)"], ["quaTangNT", "🎁 Quà 20-10 (NT)"], ["tongThuNhap", "TỔNG THU NHẬP", "B"],
  ["bhxh", "BHXH", "-"], ["congDoan", "Công đoàn", "-"], ["thueTNCN", "Thuế TNCN", "-"], ["truKhac", "Khác / Trừ", "-"], ["tamUng", "Tạm ứng", "-"], ["phiNganHang", "Phí ngân hàng", "-"],
  ["thucLinh", "THỰC LĨNH", "T"], ["soTienCK", "Số tiền chuyển khoản"],
];
const CongDaoTaoTab = ({ data }) => {
  if (!data || !data.months || !data.months.length) return (
    <div>
      <ChuaCongBoNote list={data && data.chuaCongBo} />
      <p style={{ color: "#6b7280" }}>Chưa có dữ liệu.</p>
    </div>
  );
  return (
    <div>
      <ChuaCongBoNote list={data.chuaCongBo} />
      {data.months.map((m) => (
        <div key={m.monthKey} style={{ border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
          <div style={{ background: "#f0fdfa", padding: "8px 12px", fontWeight: 700, color: "#0f766e" }}>{monthTitle(m.monthKey)}</div>
          {m.rows.map((r, i) => (
            <table key={i} style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
              <tbody>
                {m.rows.length > 1 && <tr><td colSpan={2} style={{ ...td, textAlign: "left", fontWeight: 700, background: "#f9fafb" }}>{r.hoten}</td></tr>}
                {(r.chucVu || r.nhom) && <tr><td style={{ ...td, textAlign: "left", color: "#6b7280" }}>Bộ phận / Chức vụ</td><td style={{ ...td, textAlign: "right" }}>{[String(r.nhom || "").replace(/^[IVX]+\.\s*/, ""), r.chucVu].filter(Boolean).join(" — ")}</td></tr>}
                {CDT_LINES.map(([k, label, kind]) => {
                  if (k === "cong") return r.congThoaThuan || r.congThucTe ? (<tr key={k}><td style={{ ...td, textAlign: "left" }}>{label}</td><td style={{ ...td, textAlign: "right" }}>{r.congThoaThuan || "—"} / <b>{r.congThucTe || "—"}</b></td></tr>) : null;
                  const v = Number(r[k]) || 0;
                  if (!v && !["tongThuNhap", "thucLinh"].includes(k)) return null;
                  const big = kind === "B" || kind === "T";
                  return (
                    <tr key={k} style={{ background: kind === "T" ? "#ecfdf5" : kind === "B" ? "#f9fafb" : undefined }}>
                      <td style={{ ...td, textAlign: "left", fontWeight: big || kind === "b" ? 700 : 400 }}>{label}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: big || kind === "b" ? 700 : 400, color: kind === "-" ? "#dc2626" : kind === "T" ? "#047857" : undefined, fontSize: big ? 15 : 14 }}>{kind === "-" ? "− " : ""}{vnd(v)}</td>
                    </tr>
                  );
                })}
                {r.ghiChuPhuCap && <tr><td style={{ ...td, textAlign: "left", color: "#6b7280" }}>Ghi chú phụ cấp</td><td style={{ ...td, textAlign: "right", color: "#374151" }}>{r.ghiChuPhuCap}</td></tr>}
                {r.ghiChuKhauTru && <tr><td style={{ ...td, textAlign: "left", color: "#6b7280" }}>Ghi chú khấu trừ</td><td style={{ ...td, textAlign: "right", color: "#374151" }}>{r.ghiChuKhauTru}</td></tr>}
              </tbody>
            </table>
          ))}
          {m.qtkd && m.qtkd.length > 0 && (
            <div style={{ padding: "8px 12px", borderTop: "1px solid #e5e7eb", fontSize: 14 }}>
              🏢 Lương QTKD: {m.qtkd.map((q, i) => <b key={i}>{vnd(q.soTien)}{i < m.qtkd.length - 1 ? " + " : ""}</b>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const TABS = [
  { key: "chamcong", label: "🧾 Xem chấm công" },
  { key: "luongtiet", label: "🧮 Lương và PC giảng dạy" },
  { key: "khaosat", label: "📝 Khảo sát" },
  { key: "vipham", label: "⚠️ Vi phạm" },
  { key: "thunhap", label: "💵 Thu nhập" },
];

const HappySchoolMyChamCong = () => {
  const schoolId = getActiveSchoolId();
  const [tab, setTab] = useState("chamcong");
  const [cdt, setCdt] = useState(null);
  useEffect(() => {
    if (!schoolId) return;
    getMyCongDaoTao(schoolId).then((d) => { setCdt(d); if (d && d.months && d.months.length) setTab("congdaotao"); }).catch(() => setCdt(null));
  }, [schoolId]);
  if (!schoolId) return <p style={{ color: "#6b7280" }}>Chưa chọn trường.</p>;
  const tabs = cdt && ((cdt.months && cdt.months.length) || (cdt.chuaCongBo && cdt.chuaCongBo.length)) ? [{ key: "congdaotao", label: "🏫 Thu nhập (Đào tạo)" }, ...TABS] : TABS;

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 12px" }}>💰 Xem thu nhập</h2>
      <div style={{ display: "flex", gap: 6, marginBottom: 16, borderBottom: "1px solid #e5e7eb" }}>
        {tabs.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} style={t.key === tab ? tabBtnActive : tabBtnInactive}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "congdaotao" && <CongDaoTaoTab data={cdt} />}
      {tab === "chamcong" && <ChamCongTab />}
      {tab === "luongtiet" && <LuongTheoTietTab />}
      {tab === "vipham" && <ViPhamTab />}
      {tab === "thunhap" && <ThuNhapTab />}
      {tab === "khaosat" && <KhaoSatTab />}
    </div>
  );
};

const grandBox = { display: "flex", gap: 24, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px", marginBottom: 14 };
const monthRow = { display: "flex", alignItems: "center", gap: 6, padding: "10px 12px", cursor: "pointer", background: "#fff" };
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "6px 8px", textAlign: "center" };
const tabBtnBase = { padding: "8px 14px", border: "none", borderBottom: "2px solid transparent", background: "transparent", cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#6b7280" };
const tabBtnActive = { ...tabBtnBase, color: "#0f766e", borderBottomColor: "#0f766e" };
const tabBtnInactive = tabBtnBase;
const exportBtn = { padding: "6px 12px", border: "1px solid #0ea5a3", borderRadius: 6, background: "#fff", color: "#0ea5a3", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const subTabBtnBase = { padding: "5px 10px", border: "1px solid #d1d5db", borderRadius: 16, background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#6b7280" };
const subTabBtnActive = { ...subTabBtnBase, background: "#0ea5a3", borderColor: "#0ea5a3", color: "#fff" };
const subTabBtnInactive = subTabBtnBase;

export default HappySchoolMyChamCong;
